import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableColumnsType } from "antd";
import { VideoManagerPresenter } from "./VideoManagerPresenter";
import * as s from "./VideoManagerStyled";

import {
  SelectGroup,
  VIDEO_GUIDE_TYPE,
  VideoGuideSetting,
  VideoModalState,
} from "api/interfaces/managerInterface.interface";

import useHelpGuideApi, {
  HelpGuide,
  HelpGuideGroup,
} from "hook/useHelpGuideApi";

import { videoGuideOptions } from "utils/options";
import { notify } from "components/atoms/notification/Notification";
import TableButton from "components/atoms/buttons/TableButton";
import { getAccountTimeFormat } from "utils/timeUtil";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import BigButton from "components/atoms/buttons/BigButton";
import { checkEmpty } from "utils/functions";

export default function VideoManagerContainer(): JSX.Element {
  const treeRef = useRef(null);

  const [groups, setGroups] = useState<HelpGuideGroup[]>([]);
  const [groupsIsFetching, setGroupsIsFetching] = useState<boolean>(false);
  const [videoSettingList, setVideoSettingList] = useState<VideoGuideSetting[]>(
    []
  );
  const [selectedVideo, setSelectedVideo] = useState<VideoGuideSetting>();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [sortInfo, setSortInfo] = useState<{
    order: "ascend" | "descend" | null;
    columnKey: string;
  }>({
    order: "ascend",
    columnKey: "position", // "Order" 컬럼의 dataIndex 또는 key
  });
  const [selectedGroup, setSelectedGroup] = useState<SelectGroup>({
    category: videoGuideOptions[0].value,
    groupId: "",
    groupName: "",
    layout: "slide",
    position: 0,
  });

  const [modalState, setModalState] = useState<VideoModalState>({
    groupAdd: false,
    groupEdit: false,
    videoAdd: false,
    videoEdit: false,
    pdfAdd: false,
    pdfEdit: false,
    deleteConfirm: false,
  });

  const onSort = (field: string, order: "ascend" | "descend") => {
    setSortInfo({ order, columnKey: field });
  };

  const toggleModal = (key: keyof typeof modalState, value?: boolean) => {
    setModalState((prev) => ({
      ...prev,
      [key]: value ? value : false,
    }));
  };

  const {
    getHelpGuideGroups: fetchGroups,
    getHelpGuides: fetchGuides,
    deleteHelpGuideItem,
  } = useHelpGuideApi();

  const getHelpGuideGroups = () => {
    setGroupsIsFetching(true);
    fetchGroups()
      .then((lists) => {
        setGroups(lists);
        if (!lists.some((list) => list.groupId === selectedGroup.groupId)) {
          setSelectedGroup({
            category: videoGuideOptions[0].value,
            groupId: lists[0].groupId,
            groupName: lists[0].groupName,
            layout: lists[0].layout,
            position: lists[0].sortOrder,
          });
          onClickGroup(lists[0].groupId);
        }
      })
      .finally(() => setGroupsIsFetching(false));
  };

  const getHelpGuides = () => {
    fetchGuides().then((res) =>
      setVideoSettingList(
        res.map((guide) => ({
          ...guide,
          videoUrl: guide.resourceUrl,
          resourceType: guide.resourceType,
          thumbnailUrl: guide.thumbnailUrl || "",
          description: guide.description || "",
        }))
      )
    );
  };

  const closeModal = () => {
    setModalState({
      groupAdd: false,
      groupEdit: false,
      videoAdd: false,
      videoEdit: false,
      pdfAdd: false,
      pdfEdit: false,
      deleteConfirm: false,
    });
  };

  const onClickDelete = async (item: VideoGuideSetting) => {
    const helpGuide = { ...item, resourceUrl: item.videoUrl };
    return deleteHelpGuideItem(helpGuide)
      .then(() => {
        notifyByType("success", item.resourceType, "delete");
        closeModal();
        getHelpGuides();
      })
      .catch(() => notifyByType("error", item.resourceType, "delete"));
  };

  const onModal = useCallback(
    (type: VIDEO_GUIDE_TYPE, isEdit: boolean) => {
      const modalKey =
        type === VIDEO_GUIDE_TYPE.GROUP
          ? isEdit
            ? "groupEdit"
            : "groupAdd"
          : type === VIDEO_GUIDE_TYPE.YOUTUBE
          ? isEdit
            ? "videoEdit"
            : "videoAdd"
          : isEdit
          ? "pdfEdit"
          : "pdfAdd";

      if (
        !isEdit &&
        type !== VIDEO_GUIDE_TYPE.GROUP &&
        checkEmpty(selectedGroup.groupId)
      )
        return;
      toggleModal(modalKey, !modalState[modalKey]);
      if (isEdit && type !== VIDEO_GUIDE_TYPE.GROUP) getHelpGuides();
      if (isEdit && type === VIDEO_GUIDE_TYPE.GROUP) getHelpGuideGroups();
    },
    [selectedGroup.groupId, modalState]
  );

  const onClickGroup = (id: string) => {
    const group = groups.find((g) => g.groupId === id);
    if (group) {
      setSelectedGroup({
        category: videoGuideOptions[0].value,
        groupId: id,
        groupName: group.groupName || "",
        layout: group.layout || "slide",
        position: group.sortOrder || 0,
      });
    }
  };

  const onClickEditGroup = (id: string) => {
    const group = groups.find((g) => g.groupId === id);
    if (group) {
      setSelectedGroup({
        category: videoGuideOptions[0].value,
        groupId: id,
        groupName: group.groupName || "",
        layout: group.layout || "slide",
        position: group.sortOrder || 0,
      });
      toggleModal("groupEdit", true);
    }
  };

  useEffect(() => {
    getHelpGuideGroups();
    getHelpGuides();
  }, []);

  const isSlide = selectedGroup.layout === "slide";

  const columns: TableColumnsType<VideoGuideSetting> = [
    {
      title: "Order",
      render: (_, row) => row.position,
      sorter: (a, b) => a.position - b.position,
      sortOrder: sortInfo.columnKey === "position" ? sortInfo.order : null,
      onHeaderCell: (column) => ({
        onClick: () =>
          onSort(
            "position",
            sortInfo.order === "ascend" ? "descend" : "ascend"
          ),
      }),
      width: 100,
    },
    {
      title: "Thumbnail",
      width: 160,
      render: (_, row) => (
        <img style={{ width: 130 }} src={row.thumbnailUrl} alt={row.id} />
      ),

      responsive: ["xxl"],
      hidden: !isSlide,
    },
    {
      title: "Title/Description",
      render: (_, row) => (
        <div>
          <s.Title>{row.title}</s.Title>
          {isSlide ? (
            <s.Info>Video url: {row.videoUrl}</s.Info>
          ) : (
            <s.Info>File name: {row.fileName}</s.Info>
          )}

          <s.Description>{row.description}</s.Description>
        </div>
      ),
    },
    {
      title: "Created At",
      render: (_, row) => getAccountTimeFormat(row.id, selectedAccount, true),
      sorter: (a, b) => new Date(a.id).getTime() - new Date(b.id).getTime(),
      sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
      onHeaderCell: (column) => ({
        onClick: () =>
          onSort("id", sortInfo.order === "ascend" ? "descend" : "ascend"),
      }),
      width: "15%",
      responsive: ["lg"],
    },
    {
      title: "",
      width: 160,
      render: (_, row) => (
        <s.Row>
          <TableButton
            label={"Delete"}
            buttonType="warning"
            onClick={() => {
              setSelectedVideo(row);
              toggleModal("deleteConfirm", true);
            }}
            data-testid="manager-video-manager-video-manager-table-button-set-selected-video"
          />

          <TableButton
            label={"Edit"}
            onClick={() => {
              setSelectedVideo(row);
              toggleModal(
                row.resourceType === "youtube" ? "videoEdit" : "pdfEdit",
                true
              );
            }}
            data-testid="manager-video-manager-video-manager-table-button-set-selected-video-2"
          />
        </s.Row>
      ),
    },
  ];

  const data: VideoGuideSetting[] = videoSettingList
    .filter((item) => item.groupId === selectedGroup.groupId)
    .map((item, index) => ({ ...item, key: index }))
    .sort((a, b) => a.position - b.position);

  const buttons = useMemo(() => {
    return (
      <>
        <BigButton
          label="Add Group"
          onClickBtn={() => onModal(VIDEO_GUIDE_TYPE.GROUP, false)}
          add
          buttonType="cancel"
          data-testid="manager-video-manager-video-manager-button-modal"
        />

        <BigButton
          label={selectedGroup.layout === "table" ? "Add File" : "Add Video"}
          add
          onClickBtn={() =>
            onModal(
              selectedGroup.layout === "table"
                ? VIDEO_GUIDE_TYPE.PDF
                : VIDEO_GUIDE_TYPE.YOUTUBE,
              false
            )
          }
          data-testid="manager-video-manager-video-manager-button-modal-2"
        />
      </>
    );
  }, [onModal, selectedGroup.layout]);

  return (
    <VideoManagerPresenter
      data={data}
      groups={groups}
      groupsIsFetching={groupsIsFetching}
      columns={columns}
      onModal={onModal}
      isGroupAddModal={modalState.groupAdd}
      isGroupEditModal={modalState.groupEdit}
      isVideoAddModal={modalState.videoAdd}
      isVideoEditModal={modalState.videoEdit}
      isPdfAddModal={modalState.pdfAdd}
      isPdfEditModal={modalState.pdfEdit}
      isDeleteConfirm={modalState.deleteConfirm}
      setIsDeleteConfirm={(v) => toggleModal("deleteConfirm", v)}
      treeRef={treeRef}
      selectGroup={selectedGroup}
      selectedVideo={selectedVideo}
      onClickGroup={onClickGroup}
      onClickEditGroup={onClickEditGroup}
      onClickDelete={onClickDelete}
      getHelpGuides={getHelpGuides}
      getHelpGuideGroups={getHelpGuideGroups}
      buttons={buttons}
    />
  );
}

export const buildGuideItem = (
  data: any,
  resourceType: "youtube" | "pdf" | "etc",
  file?: File
): HelpGuide => ({
  id: data.id || new Date().toISOString(),
  title: data.title,
  description: data.description,
  groupName: data.groupName || "",
  groupId: data.groupId || "",
  resourceUrl: file ? "" : data.resourceUrl || data.videoUrl || "",
  resourceType: file
    ? file.type.includes("pdf")
      ? "pdf"
      : "etc"
    : resourceType,
  position: data.position || 0,
  thumbnailUrl: data.thumbnailUrl,
  fileName: file?.name || data.fileName || "",
});
export const notifyByType = (
  type: "success" | "error",
  resourceType: string,
  action: "create" | "delete" | "update"
) => {
  const typeLabel = resourceType === "youtube" ? "Video" : "File";
  const verb =
    action === "create"
      ? type === "success"
        ? "added"
        : "add"
      : action === "delete"
      ? type === "success"
        ? "deleted"
        : "delete"
      : type === "success"
      ? "updated"
      : "update";

  notify(
    type,
    `${typeLabel} ${verb} ${type === "success" ? "successfully." : "failed."}`
  );
};
